import React, { useState, useEffect } from 'react';
import { gameService } from '../firebase/gameService';
import type { GameState, Player, Question } from '../types/game';

interface GameBoardProps {
  gameId: string;
  playerId: string;
  questions: Question[];
  round: number;
}

export const GameBoard: React.FC<GameBoardProps> = ({ gameId, playerId, questions, round }) => {
  const [game, setGame] = useState<GameState | null>(null);
  const [player, setPlayer] = useState<Player | null>(null);

  useEffect(() => {
    const unsubscribe = gameService.subscribeToGame(gameId, (updatedGame) => {
      setGame(updatedGame);
      const currentPlayer = updatedGame.players.find(p => p.id === playerId);
      setPlayer(currentPlayer || null);
    });

    return () => unsubscribe();
  }, [gameId, playerId]);

  if (!game || !player) {
    return <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-brand-yellow text-2xl">Loading...</div>
    </div>;
  }

  const buildGameBoard = (questions: Question[], round: number) => {
    const questionsByDifficulty = {
      easy: questions.filter(q => q.difficulty === 'easy'),
      medium: questions.filter(q => q.difficulty === 'medium'),
      hard: questions.filter(q => q.difficulty === 'hard')
    };

    const gameBoard = {
      round1: {
        $100: questionsByDifficulty.easy.slice(0, 1), // First easy question
        $200: questionsByDifficulty.easy.slice(1, 2), // Second easy question
        $300: questionsByDifficulty.medium.slice(0, 1), // First medium question
        $400: questionsByDifficulty.medium.slice(1, 2), // Second medium question
        $500: questionsByDifficulty.hard.slice(0, 1) // Hard question
      },
      round2: {
        $200: questionsByDifficulty.easy.slice(0, 1), // First easy question
        $400: questionsByDifficulty.easy.slice(1, 2), // Second easy question
        $600: questionsByDifficulty.medium.slice(0, 1), // First medium question
        $800: questionsByDifficulty.medium.slice(1, 2), // Second medium question
        $1000: questionsByDifficulty.hard.slice(0, 1) // Hard question
      }
    };

    return gameBoard[round === 1 ? 'round1' : 'round2'];
  };

  const gameBoard = buildGameBoard(questions, round);

  return (
    <div className="min-h-screen bg-black flex flex-col items-center py-8 px-4">
      {/* Header with Logo and Game Info */}
      <div className="w-full max-w-7xl flex justify-between items-center mb-12">
        <img src="/images/secondary-logo-white.svg" alt="Vexl Logo" className="h-16" />
        <div className="flex items-center gap-8">
          <div className="text-right">
            <p className="text-brand-yellow text-xl font-bold">Game Code</p>
            <p className="text-white text-2xl tracking-widest">{game.code}</p>
          </div>
          <div className="text-right">
            <p className="text-brand-yellow text-xl font-bold">Your Score</p>
            <p className="text-white text-2xl">${player.score}</p>
          </div>
        </div>
      </div>

      {/* Jeopardy-style 5x5 grid */}
      <div className="w-full max-w-7xl overflow-x-auto">
        <table className="border-separate border-spacing-6 w-full">
          <thead>
            <tr>
              {game.categories.map((category) => (
                <th
                  key={category.id}
                  className="text-base md:text-lg font-semibold text-center bg-brand-yellow text-black rounded-xl shadow-md uppercase whitespace-nowrap overflow-hidden text-ellipsis max-w-[160px] md:max-w-[200px] border border-[#e6b84c] tracking-wide h-16 md:h-20 align-middle p-0"
                  style={{ verticalAlign: 'middle' }}
                  title={category.name}
                >
                  <span className="flex items-center justify-center h-full w-full px-2">{category.name}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(gameBoard).map(([amount, questions]) => (
              <tr key={amount}>
                <td className="align-middle text-center">
                  <div
                    className={`relative w-44 h-28 flex items-center justify-center border-4 border-[#e6b84c] bg-black font-black text-4xl md:text-5xl uppercase tracking-widest select-none transition-all duration-200 text-center rounded-b-2xl shadow-lg drop-shadow-lg mx-auto my-2
                      ${questions[0].answered ? 'opacity-50 cursor-not-allowed bg-[#181818]' : 'text-brand-yellow cursor-pointer hover:bg-[#222] hover:scale-105 hover:shadow-2xl'}
                      ${questions[0].revealed && !questions[0].answered ? 'opacity-100' : ''}`}
                    style={{ fontFamily: 'inherit' }}
                    tabIndex={-1}
                  >
                    {questions[0].answered && (
                      <img
                        src="/images/white-glasses.svg"
                        alt="Answered"
                        className="absolute inset-0 m-auto"
                        style={{
                          width: '60%',
                          height: '60%',
                          opacity: 0.18,
                          pointerEvents: 'none',
                          zIndex: 1,
                        }}
                      />
                    )}
                    {!questions[0].answered && !questions[0].revealed && (
                      <span className="drop-shadow-[0_2px_4px_rgba(255,215,0,0.5)]">${amount}</span>
                    )}
                    {questions[0].revealed && !questions[0].answered && (
                      <span className="font-bold px-4 text-lg md:text-2xl text-white z-10">{questions[0].text}</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Player List */}
      <div className="mt-12 w-full max-w-7xl">
        <h2 className="text-2xl font-bold mb-6 text-brand-yellow flex items-center gap-2">
          <img src="/images/white-glasses.svg" alt="Players" className="h-6" />
          Players
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {game.players.map((p) => (
            <div
              key={p.id}
              className={`flex items-center gap-4 bg-[#333333] rounded-lg px-6 py-4 shadow-lg transition-all duration-300
                ${p.id === playerId ? 'ring-2 ring-brand-yellow bg-[#3d4d41]' : 'hover:bg-[#28332a]'}`}
            >
              <div className="h-12 w-12 rounded-full bg-[#28332a] flex items-center justify-center">
                <img src={`/images/${p.avatar}`} alt="Avatar" className="h-8 w-8" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-brand-yellow text-lg">{p.name}</span>
                <span className="text-white text-xl">${p.score}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Current Question Banner/Modal */}
      {game.currentQuestion && (
        <div className="mb-12 bg-[#333333] rounded-lg p-8 shadow-lg w-full max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-brand-yellow flex items-center gap-2 justify-center">
            <img src="/images/white-glasses.svg" alt="Current Question" className="h-6" />
            {game.currentQuestion.categoryName} - Current Question
          </h2>
          <div className="bg-[#3d4d41] rounded-lg p-6">
            <p className="text-white text-2xl mb-4">{game.currentQuestion.question.text}</p>
            {game.currentQuestion.question.answered && (
              <p className="text-[#acd9b7] text-xl">Answer: {game.currentQuestion.question.answer}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}; 